'use client';

import {
	MarketType,
	PerpMarketAccount,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import { MarketId, UIMarket } from '@velocity-exchange/common';
import { memo, useRef } from 'react';
import useCurrentMarketMarkPrice from 'src/hooks/useCurrentMarketMarkPrice';
import useInfoForCurrentlySelectedMarket from '../../hooks/useInfoForCurrentlySelectedMarket';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import { Rolling24HrVolumeNumberDisplay } from './Rolling24hrVolumeNumberDisplay';
import StatDisplay from './StatDisplay';
import { ViewDetailsForMarketButton } from './ViewDetailsForMarket';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { MarketOracleInfo } from '../MarketInfo/MarketOracleInfo';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import { cj } from 'src/utils/classNames';
import {
	MARKET_STATS_BAR_CELL_LABEL_CLASSNAME,
	MARKET_STATS_BAR_CELL_CLASSNAME,
	MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME,
} from 'src/components/MarketStatsBar/consts';
import FundingRateCard from 'src/components/MarketStatsBar/FundingRateCard';
import OpenInterestCard from 'src/components/MarketStatsBar/OpenInterestCard';
import MarkPriceDisplay from 'src/components/MarketStatsBar/MarkPriceDisplay';
import ScrollShadows from 'src/components/ScrollShadows';
import { useLiveOracleForDisplay } from 'src/stores/marketLiveState';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import { useCurrentMarketInfoDisplayStore } from 'src/stores/marketLiveState/marketInfoDisplay';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';

function MarketStatsBar() {
	const isMobileScreenSize = useIsMobileScreenSize();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const selectedUiMarket = useVelocityStore((s) => s.selectedMarket.current);
	const selectedMarketId = useVelocityStore((s) => s.selectedMarket.marketId);
	const selectedMarketBaseAssetSymbol = useVelocityStore(
		(s) => s.selectedMarket.current?.uiSymbols.baseAssetSymbol
	);

	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();

	const marketData = useCurrentMarketInfoDisplayStore((s) => s.data);
	const marketSymbol = selectedUiMarket?.uiSymbols.marketSymbol ?? '';

	const markPrice = useCurrentMarketMarkPrice();
	const oraclePriceToUse =
		useLiveOracleForDisplay(selectedMarketId)?.price ?? ZERO_PRICE_BIGNUM;

	const marketAccount = selectedMarketInfo?.info?.account as
		| PerpMarketAccount
		| SpotMarketAccount;

	const showOraclePrice = !oraclePriceToUse.eqZero() && selectedMarketInfo.info;

	const oracleSource = selectedMarketId?.isPerp
		? (marketAccount as PerpMarketAccount)?.oracleSource
		: (marketAccount as SpotMarketAccount)?.oracleSource;
	const oracle = selectedMarketId?.isPerp
		? (marketAccount as PerpMarketAccount)?.oracle
		: (marketAccount as SpotMarketAccount)?.oracle;

	return (
		<ScrollShadows
			className={cj(
				'relative flex size-full items-center justify-center',
				'min-w-0 flex-1 bg-container-bg',
				isMobileScreenSize && 'px-xs pb-md'
			)}
			gradientFromClassName="from-container-bg"
			scrollableRef={scrollContainerRef}
			screenSize="all"
		>
			<div
				className="hidden-scroll flex w-full items-start justify-between overflow-x-auto overflow-y-hidden whitespace-nowrap sm:items-stretch sm:justify-start"
				ref={scrollContainerRef}
			>
				{/* Mark price section — large price + 24h % change */}
				{!isMobileScreenSize && (
					<div className="px-2xl">
						<MarkPriceDisplay
							loading={!markPrice || markPrice.eqZero()}
							price={markPrice?.toString()}
							marketId={selectedMarketId}
						/>
					</div>
				)}

				<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
					<Tooltip
						decoration="cursor-help"
						content={
							<MarketOracleInfo
								marketId={selectedMarketId}
								marketData={marketData}
								oracleSource={oracleSource}
								selectedMarketBaseAssetSymbol={
									selectedMarketBaseAssetSymbol ?? ''
								}
								oracle={oracle}
								oracleDelayText={
									selectedMarketId?.isPerp
										? 'The price feed has not updated within the protocol staleness window, so AMM might be unavailable for fills.'
										: 'The price feed has not updated within the protocol staleness window.'
								}
								confidenceIntervalTooLargeText={
									selectedMarketId?.isPerp
										? 'The price feed confidence interval too large, so AMM might be unavailable for fills.'
										: 'The price feed confidence interval too large.'
								}
							/>
						}
					>
						<div className="flex flex-col">
							<StatDisplay
								header="Oracle"
								headerClassName={MARKET_STATS_BAR_CELL_LABEL_CLASSNAME}
								leftBorder
							>
								<div className="flex flex-col">
									{showOraclePrice ? (
										<div className="flex items-center text-text-default">
											<NumberDisplay
												value={oraclePriceToUse}
												options={withDecimals(
													PRESETS.usdTruncate,
													getMarketPriceDecimals(
														selectedMarketBaseAssetSymbol || ''
													)
												)}
											/>
										</div>
									) : (
										<SkeletonValuePlaceholder
											className={MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME}
											loading
										/>
									)}
								</div>
							</StatDisplay>
						</div>
					</Tooltip>
				</div>

				{selectedMarketId?.isPerp && (
					<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
						<FundingRateCard
							marketSymbol={marketSymbol}
							selectedMarketId={selectedMarketId}
							fundingRateUnit="h"
						/>
					</div>
				)}

				{selectedMarketId?.isPerp && (
					<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
						<OpenInterestCard
							marketId={selectedMarketId}
							symbol={marketSymbol}
							maxOpenInterest={marketData.maxOpenInterest}
							longOpenInterest={marketData.longOpenInterest}
							shortOpenInterest={marketData.shortOpenInterest}
							openInterest={marketData.openInterest}
							oraclePrice={oraclePriceToUse}
						/>
					</div>
				)}

				<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
					<Rolling24HrVolumeNumberDisplay
						marketIndex={selectedMarketId?.marketIndex ?? 0}
						marketType={selectedMarketId?.marketType ?? MarketType.PERP}
					/>
				</div>

				{!isMobileScreenSize && (
					<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
						<ViewDetailsForMarketButton
							market={UIMarket.fromMarketId(selectedMarketId as MarketId)}
						/>
					</div>
				)}
			</div>
		</ScrollShadows>
	);
}

export default memo(MarketStatsBar);
